import React, { ReactNode } from 'react'
import { Box, Card, CardContent, Typography, IconButton, SxProps, Theme } from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'
import { useOutletContext } from 'react-router-dom'
import ExpiredSubscriptionBanner from './common/ExpiredSubscriptionBanner'
import PendingPaymentBanner from './common/PendingPaymentBanner'
import { usePaymentContext } from '../contexts/PaymentContext'
import {
  PAGE_BANNER_CARD_SX,
  PAGE_BANNER_DECORATION_SX,
  PAGE_BANNER_CONTENT_SX,
  PAGE_BANNER_HEADING_SX,
  PAGE_BANNER_SUBHEADING_SX,
} from '../theme/constants'

export interface PageBannerProps {
  /** Main title text or custom title node */
  title: ReactNode
  /** Subtitle text or custom subtitle node */
  subtitle?: ReactNode
  /** Optional status badge or chip next to title */
  statusChip?: ReactNode
  /** Right-hand side action elements (buttons, chips, extra actions) */
  action?: ReactNode
  /** Optional refresh click handler - renders rounded refresh IconButton */
  onRefresh?: () => void
  /** Optional loading state for refresh button */
  refreshing?: boolean
  /** Extra children inside the banner box */
  children?: ReactNode
  /** Option to suppress expired subscription banner */
  hideExpiredBanner?: boolean
  /** SX overrides for the Card container */
  cardSx?: SxProps<Theme>
}

export const PageBanner: React.FC<PageBannerProps> = ({
  title,
  subtitle,
  statusChip,
  action,
  onRefresh,
  refreshing,
  hideExpiredBanner = false,
  children,
  cardSx,
}) => {
  const combinedSx: SxProps<Theme> = cardSx
    ? [PAGE_BANNER_CARD_SX, ...(Array.isArray(cardSx) ? cardSx : [cardSx])]
    : PAGE_BANNER_CARD_SX

  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const smbNavState = outletContext?.smbNavState
  const { hasPendingCheckout, loading: paymentLoading } = usePaymentContext()
  const noActiveSub = Boolean(!paymentLoading && !hideExpiredBanner && smbNavState?.isOnboarded && !smbNavState?.hasSubscription)
  // Pending banner: show on ALL pages (onboarded or not) when user has a pending payment
  const showPendingBanner = Boolean(!paymentLoading && !hideExpiredBanner && hasPendingCheckout && !smbNavState?.hasSubscription)
  // Expired banner: only for fully onboarded users with no sub and no pending payment
  const showExpiredBanner = noActiveSub && !hasPendingCheckout

  return (
    <Box>
      <Card sx={combinedSx}>
        <Box sx={PAGE_BANNER_DECORATION_SX} />
        <CardContent sx={PAGE_BANNER_CONTENT_SX}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 2, sm: 2 },
            width: '100%',
            position: 'relative',
            zIndex: 1,
          }}>
            {/* Title & Subtitle Info (With Mobile Top-Right Refresh Icon) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {typeof title === 'string' ? (
                    <Typography sx={{ ...PAGE_BANNER_HEADING_SX, mb: 0 }}>
                      {title}
                    </Typography>
                  ) : (
                    title
                  )}
                  {statusChip}
                </Box>

                {/* Mobile Top Right Refresh Button */}
                {onRefresh && (
                  <IconButton
                    onClick={onRefresh}
                    disabled={refreshing}
                    sx={{
                      display: { xs: 'inline-flex', sm: 'none' },
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                      '&.Mui-disabled': {
                        color: refreshing ? 'white' : 'rgba(255,255,255,0.3)',
                        bgcolor: 'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    <RefreshIcon
                      sx={{
                        animation: refreshing ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                )}
              </Box>

              {typeof subtitle === 'string' && subtitle ? (
                <Typography sx={{ ...PAGE_BANNER_SUBHEADING_SX, mt: 0.5 }}>
                  {subtitle}
                </Typography>
              ) : subtitle ? (
                subtitle
              ) : (
                <Typography sx={{ ...PAGE_BANNER_SUBHEADING_SX, mt: 0.5, visibility: 'hidden' }}>
                  {'\u00A0'}
                </Typography>
              )}
            </Box>

          {/* Action Buttons & Desktop Refresh Button */}
          {(action || onRefresh) && (
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              alignItems: { xs: 'stretch', sm: 'center' },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: 'flex-end',
              '& > button, & > span, & > a': {
                width: { xs: '100%', sm: 'auto' },
              }
            }}>
              {action}
              {onRefresh && (
                <IconButton
                  onClick={onRefresh}
                  disabled={refreshing}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-disabled': {
                      color: refreshing ? 'white' : 'rgba(255,255,255,0.3)',
                      bgcolor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              )}
            </Box>
          )}

            {children}
          </Box>
        </CardContent>
      </Card>
      {showPendingBanner && <PendingPaymentBanner sx={{ mt: 2.5, mb: 3 }} />}
      {showExpiredBanner && <ExpiredSubscriptionBanner sx={{ mt: 2.5, mb: 3 }} />}
    </Box>
  )
}

export default PageBanner
